import { observer } from 'mobx-react';
import * as React from 'react';
import { AgendaItem } from '../store/schema/AgendaItem';
import { DateRange } from 'owa-datetime-utils';
import { getScenarioActions } from '../actions/getScenarioActions';
import { getAgendaLockedCalendarEventsReader } from '../selectors/calendarEventsLockedStoreSelectors';
import { DisplayDate } from 'owa-datetime';
import { AgendaEventViewController } from './AgendaEventViewController';
import { ListWithScrollToIndex } from './ListWithScrollToIndex';
import { EventsEmptyState } from './EmptyState';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { getScenarioSelectors } from '../selectors/agendaStoreSelectors';
import { AgendaEventsLoadingListView } from 'owa-agenda-loading-events';
import { initializePane } from '../actions/internalActions';

interface AgendaEventListViewState {
    time: DisplayDate;
}
export interface AgendaEventsListViewProps {
    dateRange: DateRange;
    folderIds: string[];
    scenarioId: string;
}
const styles = require('./AgendaEventsListView.scss');

@observer
export class AgendaEventsListView extends React.Component<
    AgendaEventsListViewProps,
    AgendaEventListViewState
> {
    constructor(props: AgendaEventsListViewProps) {
        super(props);
        initializePane(props.scenarioId);
    }

    componentWillUnmount() {
        const { cancelRefreshUpNextPoll } = getScenarioActions(this.props.scenarioId);
        cancelRefreshUpNextPoll();
    }

    componentDidMount() {
        const { folderIds, dateRange, scenarioId } = this.props;
        const { loadEventsForDateRange, initializeRefreshUpNextPoll } = getScenarioActions(
            scenarioId
        );
        loadEventsForDateRange(folderIds, dateRange);
        initializeRefreshUpNextPoll(folderIds, dateRange);
    }

    componentDidUpdate(prevProps: AgendaEventsListViewProps) {
        const { dateRange, folderIds, scenarioId } = this.props;
        const {
            loadEventsForDateRange,
            initializeRefreshUpNextPoll,
            updateSelectedCalendarEvent,
            refreshUpNextCalendarEventAction,
        } = getScenarioActions(scenarioId);

        /** if the list of folders, or date range changes,
         * we want to load the events for that folder list/date range
         * if the component update was triggered by a non-prop change (a.k.a. new events
         * loaded) then we do not want to load events. */
        if (
            this.dateRangeChanged(dateRange, prevProps.dateRange) ||
            this.folderIdsChanged(folderIds, prevProps.folderIds)
        ) {
            loadEventsForDateRange(folderIds, dateRange);
            initializeRefreshUpNextPoll(folderIds, dateRange);
        }
        refreshUpNextCalendarEventAction(folderIds);

        // resetting the current selected event when the date range is changed.
        if (this.dateRangeChanged(dateRange, prevProps.dateRange)) {
            updateSelectedCalendarEvent(null);
        }
    }

    render() {
        const { folderIds, dateRange, scenarioId } = this.props;
        const {
            upNextCalendarEventIndex,
            isLoadingInitialData,
            getEventLockCacheId,
        } = getScenarioSelectors(scenarioId);
        const lockId = getEventLockCacheId();
        const { getCalendarAgendaItems } = getAgendaLockedCalendarEventsReader(lockId);
        const agendaItems = getCalendarAgendaItems(folderIds, dateRange);
        const upNext = upNextCalendarEventIndex();
        return (
            <AgendaEventsLoadingListView
                className={styles.loadingListViewWrapper}
                isDataLoaded={!isLoadingInitialData()}>
                <div className={styles.list}>
                    {!isLoadingInitialData() && (
                        <FocusZone
                            className={styles.eventsListViewZone}
                            direction={FocusZoneDirection.vertical}>
                            <ListWithScrollToIndex
                                items={agendaItems}
                                onRenderCell={this.renderCalendarEvent}
                                scrollToIndex={upNext}
                                className={
                                    upNext === 0 ? styles.beforeUpNextList : null
                                    /**
                                     * if the 0th element is the upnext element add a margin to the top of the
                                     * list to fit the up next time-until badge
                                     */
                                }
                            />
                            {agendaItems.length === 0 && <EventsEmptyState />}
                        </FocusZone>
                    )}
                </div>
            </AgendaEventsLoadingListView>
        );
    }

    private folderIdsChanged(prevFolderIds: string[], folderIds: string[]): boolean {
        if (folderIds.length !== prevFolderIds.length) {
            return true;
        }
        prevFolderIds.sort();
        folderIds.sort();
        const folderIdsAreEqual = folderIds.every((value, index) => value === prevFolderIds[index]);
        return !folderIdsAreEqual;
    }

    private dateRangeChanged(dateRange: DateRange, prevDateRange: DateRange) {
        return dateRange.start !== prevDateRange.start || dateRange.end !== prevDateRange.end;
    }

    private renderCalendarEvent = (agendaItem: AgendaItem, index: number): JSX.Element => {
        const { scenarioId } = this.props;
        const { isBeforeUpNextCalendarEvent } = getScenarioSelectors(scenarioId);
        return (
            <AgendaEventViewController
                key={agendaItem.id.Id}
                agendaItem={agendaItem}
                className={
                    isBeforeUpNextCalendarEvent(index) ? styles.beforeUpNextElement : null
                    /**
                     * if the element preceeds the upnext element, add padding the bottom of the element
                     * to leave room for the upnext elements time-until badge
                     */
                }
                scenarioId={scenarioId}
            />
        );
    };
}
